import React, {
  useMemo,
  useState,
  useCallback,
  useContext,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import format from 'string-template';

import useToast from '../../hooks/useToast';
import useStorage from '../../hooks/useStorage';
import { filterUnsupportedFiles, getUnsupportedFileTypes } from '../../../utils/file';
import { MAX_FILE_UPLOAD_SIZE, MAX_FILE_UPLOAD_SIZE_MB } from '../../../utils/files';
import { toDisplayableFile } from '../../../utils/dataProcessing';
import ChatChannelContext from '../ChatChannelContext';
import ChatContext from '../ChatContext';
import MessageInputContext, { initialState } from './MessageInputContext';
import texts from './texts.json';

const unsupportedAudioFormats = [
  'audio/x-m4a',
];

const MessageInputContextProvider = ({
  children,
}) => {
  const [attachments, setAttachments] = useState(initialState.attachments);
  const [isVoiceMemoRecording, setIsVoiceMemoRecording] = useState(initialState.isVoiceMemoRecording);

  const { mediaRecorder } = useContext(ChatContext);
  const {
    activeChannel,
    setQuotedMessage,
  } = useContext(ChatChannelContext);

  const { showToast } = useToast();
  const { getBlobFilesFromStoragePathsArray } = useStorage();

  const messageInputRef = useRef(null);

  const onAddAttachments = useCallback(async (
    fileList,
    setCommentAttachments = () => {},
    isFromCommentInput = false,
  ) => {
    const filesArray = Object.values(fileList);

    // Check for unsupported files
    const { hasUnsupportedFiles, filteredFiles } = filterUnsupportedFiles(filesArray);
    if (hasUnsupportedFiles) {
      const fileTypes = getUnsupportedFileTypes();
      showToast(format(texts.unsupportedFileError, { fileTypes }), { error: true });
    }

    if (!filteredFiles.length) {
      return;
    }

    const validFiles = filteredFiles.every((file) => file.size < MAX_FILE_UPLOAD_SIZE);
    if (validFiles) {
      const finalFileList = await Promise.all(filteredFiles.map(async (file) => {
        let newFile = file;
        if (unsupportedAudioFormats.includes(file.type)) {
          newFile = new File([file], `${file.name}`, {
            type: 'video/mp4',
            lastModified: Date.now(),
          });
        } else {
          // The composer previews attachments before they are sent, and a browser that
          // cannot decode HEIC draws nothing. Replacing the File rather than just the
          // preview url keeps the name in step, since the upload reads its extension.
          newFile = await toDisplayableFile(file);
        }
        return {
          file: newFile,
          id: `${isFromCommentInput ? 'comment' : activeChannel.id}-${uuidv4()}`,
          url: URL.createObjectURL(newFile),
        };
      }));

      if (isFromCommentInput) {
        setCommentAttachments((prev) => [...prev, ...finalFileList]);
      } else {
        setAttachments((prev) => ({
          ...prev,
          [activeChannel.id]: [...(prev[activeChannel.id] || []), ...finalFileList],
        }));
      }
    } else {
      const errorMessage = format(texts.fileSizeError, {
        maxFileSize: MAX_FILE_UPLOAD_SIZE_MB,
      });
      showToast(errorMessage, { error: true });
    }
  }, [
    activeChannel,
    showToast,
  ]);

  const onLoadAttachments = useCallback(async (attachmentList) => {
    const attachmentsAssetPaths = attachmentList.map(({ assetPath }) => assetPath);
    const loadedFiles = await getBlobFilesFromStoragePathsArray(attachmentsAssetPaths);
    if (loadedFiles.length > 0) {
      await onAddAttachments(loadedFiles);
    }
  }, [getBlobFilesFromStoragePathsArray, onAddAttachments]);

  const onRemoveAttachment = useCallback((attachmentId) => {
    setAttachments((prev) => {
      const channelAttachments = prev[activeChannel?.id] || [];
      const updatedAttachments = channelAttachments.filter((attachment) => attachment.id !== attachmentId);
      return {
        ...prev,
        [activeChannel?.id]: updatedAttachments,
      };
    });
  }, [activeChannel]);

  const reset = useCallback(() => {
    setAttachments(initialState.attachments);
  }, []);

  const onDataAvailable = useCallback((audioFile) => {
    const id = `Voice-Recording-${new Date().toTimeString().split(' ')[0]}.${audioFile.data.type.split('/')[1]}`;
    const file = new File(
      [audioFile.data],
      id,
      {
        type: audioFile.data.type,
        lastModified: Date.now(),
      },
    );
    onAddAttachments([file]);
  }, [
    onAddAttachments,
  ]);

  const startVoiceRecording = useCallback(async () => {
    await mediaRecorder.startRecording(onDataAvailable);
    if (mediaRecorder.permissionsGranted) {
      setIsVoiceMemoRecording(true);
    }
  }, [
    mediaRecorder,
    onDataAvailable,
  ]);

  const stopVoiceRecording = useCallback(() => {
    mediaRecorder.stopRecording(onDataAvailable);
    setIsVoiceMemoRecording(false);
  }, [
    mediaRecorder,
    onDataAvailable,
  ]);

  const setReply = useCallback((message, closeActionMenu) => {
    setQuotedMessage(message);
    if (closeActionMenu) closeActionMenu();
    messageInputRef.current?.focus();
  }, [setQuotedMessage]);

  const context = useMemo(() => ({
    attachments,
    onAddAttachments,
    onLoadAttachments,
    onRemoveAttachment,
    reset,
    isVoiceMemoRecording,
    startVoiceRecording,
    stopVoiceRecording,
    messageInputRef,
    setReply,
  }), [
    attachments,
    onAddAttachments,
    onLoadAttachments,
    onRemoveAttachment,
    reset,
    isVoiceMemoRecording,
    startVoiceRecording,
    stopVoiceRecording,
    messageInputRef,
    setReply,
  ]);

  return (
    <MessageInputContext.Provider value={context}>
      {children}
    </MessageInputContext.Provider>
  );
};

MessageInputContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default MessageInputContextProvider;
